import { AuthService } from '@abp/ng.core';
import { abpOAuthGuard } from '@abp/ng.oauth';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LoginLoopGuardService } from '../services/login-loop-guard.service';

/**
 * Wraps ABP's `abpOAuthGuard` so a login that can never succeed stops instead of bouncing the browser
 * between the app and the authorization endpoint forever. On the third consecutive redirect the user
 * is sent to a page that explains what to check.
 *
 * Note it delegates to `abpOAuthGuard` from @abp/ng.oauth, never to `authGuard` from @abp/ng.core:
 * the latter is a stub that logs "You should add @abp/ng-oauth packages" and returns false, which
 * cancels the navigation and leaves the route unmounted (blank screen, no requests at all).
 */
export const sessionHealthGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const loopGuard = inject(LoginLoopGuardService);
  const router = inject(Router);

  if (auth.isAuthenticated) {
    loopGuard.clear();
    return true;
  }

  if (loopGuard.isLoopDetected()) {
    return router.createUrlTree(['/session-error']);
  }

  // abpOAuthGuard polls for up to 3 seconds waiting for the token to be restored before it gives up
  // and navigates to login, so the redirect is counted only once it actually decided to leave.
  // Counting it upfront would burn an attempt on every normal bootstrap.
  return Promise.resolve(abpOAuthGuard(route, state) as boolean | Promise<boolean>).then(allowed => {
    if (!allowed) {
      loopGuard.registerLoginRedirect();
    }

    return allowed;
  });
};
